import { supabase } from "@/integrations/supabase/client";

export interface TrustStats {
  trusted_count: number;
  is_trusted: boolean;
  can_trust_others: boolean;
  has_trusted_target: boolean;
}

export interface UserTrust {
  id: string;
  trusted_by: string;
  trusted_user: string;
  created_at: string;
  comment?: string;
}

export const trustService = {
  // Get trust statistics for a user
  async getTrustStats(userId: string): Promise<TrustStats | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_user_trust_stats', { target_user_id: userId });

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error fetching trust stats:', error);
      return null;
    }
  },

  // Trust a user
  async trustUser(userId: string, comment?: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_trusts')
        .insert({
          trusted_by: user.id,
          trusted_user: userId,
          comment: comment?.trim() || null
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error trusting user:', error);
      return false;
    }
  },

  // Get trusts given by current user
  async getMyTrusts(): Promise<UserTrust[]> {
    try {
      const { data, error } = await supabase
        .from('user_trusts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching my trusts:', error);
      return [];
    }
  },

  // Get users who trusted the target user
  async getTrustGivers(userId: string): Promise<{ user_id: string; display_name: string; photo_urls: string[] }[]> {
    try {
      // First get the trust records
      const { data: trustData, error: trustError } = await supabase
        .from('user_trusts')
        .select('trusted_by')
        .eq('trusted_user', userId)
        .order('created_at', { ascending: false });

      if (trustError) throw trustError;
      if (!trustData || trustData.length === 0) return [];

      // Then get the profile data for the users who gave trust
      const trustedByIds = trustData.map(trust => trust.trusted_by);
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, photo_urls')
        .in('user_id', trustedByIds);

      if (profileError) throw profileError;
      
      return profileData?.map(profile => ({
        user_id: profile.user_id,
        display_name: profile.display_name || 'Unknown User',
        photo_urls: profile.photo_urls || []
      })) || [];
    } catch (error) {
      console.error('Error fetching trust givers:', error);
      return [];
    }
  }
};